import React, { ButtonHTMLAttributes, forwardRef } from 'react';
import classnames from 'classnames';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme, Colors } from '@app/theme';
import mapValues from 'lodash/mapValues';

const useStyles = createUseStyles((theme: Theme) => ({
  button: {
    ...theme.flexCenter,
    height: '100%',
    minWidth: theme.height.regular,
    minHeight: theme.height.regular,

    fontSize: 20,
    padding: theme.marginBase,

    color: theme.colors.green,

    border: {
      width: 1,
      style: 'solid',
      color: theme.colors.grayBorder,
    },

    borderRightWidth: 0,

    '&:first-child': {
      borderTopLeftRadius: theme.borderRadius.std,
      borderBottomLeftRadius: theme.borderRadius.std,
    },
    '&:last-child': {
      borderTopRightRadius: theme.borderRadius.std,
      borderBottomRightRadius: theme.borderRadius.std,
      borderRightWidth: 1,
    },
    '&:not([disabled]):active': {
      backgroundColor: theme.hoverColors.white,
    },
    '&:disabled': {
      opacity: 0.4,
      backgroundColor: theme.colors.white,
    },
  },
  buttonSmall: {
    minWidth: theme.height.small,
    minHeight: theme.height.small,
  },
}));

const useColorStyles = createUseStyles((theme: Theme) => {
  return mapValues(theme.colorDefinitions, ({ std }) => ({
    color: std,
  }));
});

const useActiveStyles = createUseStyles((theme: Theme) => {
  return mapValues(theme.colorDefinitions, ({ muted }) => ({
    backgroundColor: muted,
  }));
});

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon?: string;
  color?: Colors;
  active?: boolean;
  small?: boolean;
}

const IconButtonGroupMethod = (
  { className, color = 'ink', icon, active, small, ...props }: Props,
  ref: any,
) => {
  const theme: Theme = useTheme() as Theme;
  const classes = useStyles({ theme });
  const colorsClasses: any = useColorStyles({ theme });
  const activeColorsClasses: any = useActiveStyles({ theme });

  return (
    <button
      type="button"
      ref={ref}
      {...props}
      className={classnames(
        classes.button,
        colorsClasses[color],
        {
          [activeColorsClasses[color]]: !!active,
          [classes.buttonSmall]: !!small,
        },
        className,
      )}
    >
      {icon ? <i className={icon} /> : props.children}
    </button>
  );
};

export const IconButtonGroup = forwardRef(IconButtonGroupMethod);
